/**
 * useKioskHeartbeat Hook
 * 
 * Sends periodic heartbeat signals from kiosk to server.
 * This allows monitoring which kiosks are online and their last activity.
 * 
 * If heartbeat consistently fails due to monitor deletion/deactivation,
 * automatically redirects to the setup page after multiple retries.
 * Network errors are tolerated - kiosk will retry when network returns.
 * 
 * @module kiosk/hooks/useKioskHeartbeat
 */

import { useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
    getKioskToken,
    decodeJwtPayload,
    clearKioskSession,
    KIOSK_INTERVALS
} from '../utils/kioskUtils';

// Send the kiosk JWT in Authorization so the heartbeat RPC runs as role=kiosk, not anon.
const metaEnv =
    typeof import.meta !== 'undefined' ? (import.meta as any).env : undefined;
const supabaseUrl =
    (metaEnv && metaEnv.VITE_SUPABASE_URL) ||
    (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_URL : undefined);
const supabaseAnonKey =
    (metaEnv && metaEnv.VITE_SUPABASE_ANON_KEY) ||
    (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_ANON_KEY : undefined);

// ============================================================================
// Constants
// ============================================================================

/** Number of consecutive failures before clearing session */
const MAX_FAILURES_BEFORE_LOGOUT = 10;

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Sends periodic heartbeat signals to update monitor status.
 * 
 * @param enabled - Whether heartbeat is enabled (default: true)
 * @param intervalMs - Heartbeat interval in milliseconds (default: 30000)
 */
export function useKioskHeartbeat(
    enabled: boolean = true,
    intervalMs: number = KIOSK_INTERVALS.HEARTBEAT
): void {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const failureCountRef = useRef<number>(0);

    useEffect(() => {
        // Don't start heartbeat if disabled
        if (!enabled) {
            return;
        }

        const sendHeartbeat = async (): Promise<void> => {
            try {
                // Get the kiosk JWT from localStorage
                const jwt = getKioskToken();
                if (!jwt) {
                    return;
                }

                // Decode the JWT to get the monitor_id
                const payload = decodeJwtPayload(jwt);
                if (!payload) {
                    return;
                }

                const monitorId = payload.monitor_id || payload.kiosk_id || payload.sub;
                if (!monitorId) {
                    return;
                }

                if (!supabaseUrl || !supabaseAnonKey) {
                    return;
                }

                const kioskClient = createClient(supabaseUrl, supabaseAnonKey, {
                    global: {
                        headers: {
                            Authorization: `Bearer ${jwt}`,
                        },
                    },
                });

                const { data, error } = await kioskClient.rpc('fn_kiosk_heartbeat', {
                    p_monitor_id: monitorId
                });

                if (error) {
                    // Check if this is a network error vs a real server error
                    const isNetworkError = error.message?.includes('Failed to fetch') ||
                        error.message?.includes('NetworkError') ||
                        error.message?.includes('network') ||
                        error.code === 'PGRST000';

                    if (isNetworkError) {
                        // Network error - just log and wait for retry
                        console.warn('[Kiosk Heartbeat] Network error, will retry:', error.message);
                        return;
                    }

                    // Real error - increment failure counter
                    failureCountRef.current++;
                    console.error(`[Kiosk Heartbeat] Failed (${failureCountRef.current}/${MAX_FAILURES_BEFORE_LOGOUT}):`, error.message);

                    // Only clear session after multiple consecutive failures
                    if (failureCountRef.current >= MAX_FAILURES_BEFORE_LOGOUT) {
                        console.error('[Kiosk Heartbeat] Max failures reached, clearing session');
                        clearKioskSession();
                    }
                    return;
                }

                if (data?.status === 'error') {
                    // The heartbeat fn now tags terminal states (admin
                    // delete / soft-revoke) with a specific `code` so we can
                    // clear the session immediately instead of waiting for
                    // MAX_FAILURES_BEFORE_LOGOUT (10 cycles ≈ 5 min).
                    // 'heartbeat_failed' and missing/legacy `code` field
                    // remain ambiguous → keep the retry-then-clear pathway.
                    const code = (data as { code?: string }).code;

                    if (code === 'monitor_deleted' || code === 'monitor_deactivated') {
                        console.warn(
                            '[Kiosk Heartbeat] Monitor revoked by admin, clearing session immediately:',
                            code,
                        );
                        clearKioskSession();
                        return;
                    }

                    // Ambiguous error — retry until MAX_FAILURES_BEFORE_LOGOUT.
                    failureCountRef.current++;
                    console.error(
                        `[Kiosk Heartbeat] Error (${failureCountRef.current}/${MAX_FAILURES_BEFORE_LOGOUT}):`,
                        data.message,
                    );

                    if (failureCountRef.current >= MAX_FAILURES_BEFORE_LOGOUT) {
                        console.error('[Kiosk Heartbeat] Max failures reached, clearing session');
                        clearKioskSession();
                    }
                    return;
                }

                // Success - reset failure counter
                failureCountRef.current = 0;
            } catch (err) {
                // Catch block typically catches network errors
                console.warn('[Kiosk Heartbeat] Exception (network?), will retry:', err);
                // Don't clear session for exceptions - likely network issues
            }
        };

        // Send initial heartbeat immediately
        sendHeartbeat();

        // Set up interval for periodic heartbeats
        intervalRef.current = setInterval(sendHeartbeat, intervalMs);

        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [enabled, intervalMs]);
}

